import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { isCampaignViewStale } from '@/lib/campaign-view-freshness';

/**
 * Refetch the campaign when a campaign page shows settled tasks, unless its cached copy is fresh (#6826).
 *
 * Replaces the overview's and ad page's refetch-on-settled effects, which rebuilt the whole campaign on the server
 * two or three times per page view. ``settled`` is the condition those effects refetched on, so this never fetches
 * where they did not; it runs when the page opens and whenever ``settled`` turns true. A fetch already in flight
 * counts as fresh, so a StrictMode remount, a running poll, or a refetch started by an effect declared before this
 * hook never adds a request.
 */
export function useRefreshCampaignOnView(campaignSlug: string, settled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!settled) {
      return;
    }
    const state = queryClient.getQueryState(['campaign', campaignSlug]);
    if (state?.fetchStatus === 'fetching' || !isCampaignViewStale(state?.dataUpdatedAt ?? 0, Date.now())) {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['campaign', campaignSlug] });
  }, [queryClient, campaignSlug, settled]);
}
